import React from 'react';
import { motion } from 'framer-motion';
import { PhoneCall, CalendarCheck, Truck, ArrowRight } from 'lucide-react';
const steps = [{
  number: '01',
  icon: PhoneCall,
  title: 'Tell Us What You Have',
  desc: 'Call or text a photo. We give you a straight, free estimate — usually in minutes, without a site visit.'
}, {
  number: '02',
  icon: CalendarCheck,
  title: 'We Lock In A Time',
  desc: 'Often same-day or next-day. We show up when we say we will, with the right crew and the right equipment.'
}, {
  number: '03',
  icon: Truck,
  title: 'Everything Gone',
  desc: 'We haul, demo, sweep, and dispose. You do nothing. The space is left clear and ready to use.'
}];
export function SolutionIntro() {
  return <section className="py-20 lg:py-28 bg-[#F9F9F9] relative overflow-hidden" data-id="element-561" data-mp="d047vv9tnoeg">
      <div className="absolute top-1/2 -right-32 w-[420px] h-[420px] bg-[#C8102E] opacity-[0.06] blur-[130px] rounded-full pointer-events-none" data-id="element-562" data-mp="2so47shk6mcds" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-563" data-mp="2x9e8grsha5ie">
        {/* Heading */}
        <div className="max-w-3xl mb-14" data-id="element-564" data-mp="2t8m1k572wcpa">
          <motion.span initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-block text-[11px] tracking-[0.25em] font-bold uppercase text-[#C8102E] mb-4" data-id="element-565" data-mp="2t9x1tv1bf2bn">
            Introducing The One-Call Cleanout
          </motion.span>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-[1.05] mb-6" data-id="element-566" data-mp="3c76m1eegsjty">
            JOSE'S FULL-SERVICE
            <br data-id="element-567" data-mp="3rwomjlis2si" />
            <span className="text-[#C8102E]" data-id="element-568" data-mp="3pai8nad7tbv">REMOVAL SYSTEM</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.08
        }} className="text-lg text-gray-600 leading-relaxed" data-id="element-569" data-mp="3uz950wekwc7d">
            We're not your average junk hauler. We're Monterey's only true
            full-service removal crew — from a single old mattress to a complete
            construction site cleanout, handled in three steps.
          </motion.p>
        </div>

        {/* Process */}
        <ol className="grid md:grid-cols-3 gap-5 mb-12" data-id="element-570" data-mp="tkt0ns4pgyr7">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <motion.li key={step.number} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1,
            duration: 0.5,
            ease: [0.23, 1, 0.32, 1]
          }} className="relative group bg-white rounded-2xl border border-gray-200 p-8 flex flex-col hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-id="element-571" data-mp="ykp8y3kfrcck" data-mpi={i}>
                {/* Connector arrow between steps on desktop */}
                {i < steps.length - 1 && <span className="hidden md:flex absolute top-1/2 -right-[18px] z-10 w-9 h-9 rounded-full bg-[#F9F9F9] border border-gray-200 items-center justify-center -translate-y-1/2" data-id="element-572" data-mp="2j39el9rrorf7">
                    <ArrowRight size={15} className="text-gray-400" data-id="element-573" data-mp="3bqm3hqlehbbo" />
                  </span>}

                <div className="flex items-center justify-between mb-7" data-id="element-574" data-mp="36qhkntx0s4ia">
                  <span className="w-14 h-14 rounded-xl bg-[#C8102E] text-white flex items-center justify-center shadow-[0_8px_20px_rgba(200,16,46,0.28)]" data-id="element-575" data-mp="31t7olvf4kulh">
                    <Icon size={24} data-id="element-576" data-mp="1vaj4l63fci7k" />
                  </span>
                  <span className="font-oswald text-5xl font-bold text-gray-100 leading-none group-hover:text-[#C8102E]/15 transition-colors" data-id="element-577" data-mp="1qchl4k236gvk">
                    {step.number}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-578" data-mp="3jzrka2ygp1ih">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-[15px]" data-id="element-579" data-mp="357ppuyf5v0pu">
                  {step.desc}
                </p>
              </motion.li>;
        })}
        </ol>

        {/* Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="flex flex-col sm:flex-row items-center justify-between gap-5 bg-[#0E0E0E] rounded-2xl px-8 py-7" data-id="element-580" data-mp="6rwt26mm27di">
          <p className="font-oswald text-2xl md:text-3xl text-white text-center sm:text-left leading-tight" data-id="element-581" data-mp="3sxabxq6m2e3c">
            ONE CALL. ONE TEAM.{' '}
            <span className="text-[#C8102E]" data-id="element-582" data-mp="2hkhm4kxdrzpx">EVERYTHING GONE.</span>
          </p>
          <a href="tel:8312407401" className="flex-shrink-0 inline-flex items-center gap-2 bg-[#C8102E] text-white px-6 py-3.5 rounded-xl font-bold hover:bg-[#8B0A20] transition-colors" data-id="element-583" data-mp="zmcxlls0r1k7">
            <PhoneCall size={18} data-id="element-584" data-mp="18nb01zmt710o" />
            <span data-id="element-585" data-mp="18mhtaynuobij">Start With A Free Quote</span>
          </a>
        </motion.div>
      </div>
    </section>;
}